import { useEffect, useRef, useState } from "react";
import { ApiError, del, get, patch, post } from "../../api/client.ts";
import { useBrand } from "../../state/BrandContext.tsx";
import { AppSelect } from "../../components/AppSelect.tsx";

type Role = "owner" | "member" | "strategist";
interface Member { userId: string; name: string | null; email: string | null; role: Role; addedBy: "brand" | "aditor"; }
interface Invite { id: string; email: string; role: "owner" | "member"; expiresAt: string; }
interface Team { members: Member[]; invites: Invite[]; }
interface InviteResult { invite: Invite; inviteUrl: string; mailSent: boolean; }

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function errorText(e: unknown): string {
  if (!(e instanceof ApiError)) return "The connection was interrupted. Check the updated list before sending again.";
  const code = (e.body as { error?: string; retryAfter?: number } | null)?.error;
  if (code === "already_member") return "This person already has access to this brand.";
  if (code === "last_owner") return "Make someone else an owner first.";
  if (code === "owner_only") return "Only an owner can manage this team. Refresh the page to see your current role.";
  if (code === "rate_limited") return `Please wait ${(e.body as { retryAfter?: number }).retryAfter ?? 60} seconds before resending.`;
  if (code === "not_found") return "This invitation or member is no longer available. The list has been refreshed.";
  return e.message;
}

export function TeamScreen() {
  const { brand, refresh } = useBrand();
  const [team, setTeam] = useState<Team | null>(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"member" | "owner">("member");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [share, setShare] = useState<InviteResult | null>(null);
  const [copyFailed, setCopyFailed] = useState(false);
  const [retryAt, setRetryAt] = useState(0);
  const linkField = useRef<HTMLInputElement>(null);
  const owner = brand?.role === "owner";

  async function load(): Promise<void> {
    setTeam(await get<Team>("/api/team"));
  }

  useEffect(() => {
    let live = true;
    void get<Team>("/api/team").then(t => { if (live) setTeam(t); }).catch(e => { if (live) setError(errorText(e)); });
    return () => { live = false; };
  }, [brand?.id]);

  useEffect(() => {
    if (!share) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setShare(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [share]);

  useEffect(() => {
    if (!retryAt) return;
    const timer = window.setTimeout(() => setRetryAt(0), Math.max(0, retryAt - Date.now()));
    return () => window.clearTimeout(timer);
  }, [retryAt]);

  async function send(targetEmail = email, targetRole = role): Promise<void> {
    const normalized = targetEmail.trim().toLowerCase();
    if (!EMAIL.test(normalized)) { setError("Enter a valid email address."); return; }
    if (Date.now() < retryAt) return;
    setBusy(true); setError(""); setCopyFailed(false);
    try {
      const result = await post<InviteResult>("/api/team/invites", { email: normalized, role: targetRole });
      setShare(result);
      setEmail("");
      await load();
    } catch (e) {
      if (e instanceof ApiError && (e.body as { error?: string } | null)?.error === "rate_limited") {
        setRetryAt(Date.now() + ((e.body as { retryAfter?: number }).retryAfter ?? 60) * 1000);
      }
      setError(errorText(e));
      try { await load(); } catch { /* Preserve the form and uncertainty. */ }
    } finally { setBusy(false); }
  }

  async function changeMember(member: Member, action: "remove" | "role"): Promise<void> {
    if (!brand || busy) return;
    const self = member.userId === brand.self?.id;
    if (action === "remove" && !window.confirm(self
      ? `Leave ${brand.name}? You will lose access to this brand.`
      : `Remove ${member.name ?? member.email} from ${brand.name}? Other brand access stays unchanged and future delivery to this brand stops.`)) return;
    setBusy(true); setError("");
    try {
      if (action === "remove") await del(`/api/team/members/${encodeURIComponent(member.userId)}`);
      else await patch(`/api/team/members/${encodeURIComponent(member.userId)}`, { role: member.role === "owner" ? "member" : "owner" });
      if (self) await refresh();
      else await load();
    } catch (e) { setError(errorText(e)); try { await load(); } catch { /* Keep current list. */ } }
    finally { setBusy(false); }
  }

  async function revoke(invite: Invite): Promise<void> {
    if (busy || !window.confirm(`Revoke the invitation for ${invite.email} to ${brand?.name}?`)) return;
    setBusy(true); setError("");
    try { await del(`/api/team/invites/${encodeURIComponent(invite.id)}`); await load(); }
    catch (e) { setError(errorText(e)); try { await load(); } catch { /* Keep current list. */ } }
    finally { setBusy(false); }
  }

  async function copyLink(): Promise<void> {
    if (!share) return;
    try { await navigator.clipboard.writeText(share.inviteUrl); setCopyFailed(false); }
    catch { setCopyFailed(true); linkField.current?.select(); }
  }

  return <section className="team-screen" aria-label="Team">
    <header className="page-head"><h1 className="t-h2">Team</h1><p className="t-body">People who can brief and confirm work for {brand?.name}.</p></header>
    {error && <p className="team-error" role="alert">{error}</p>}
    {owner && <form className="card team-invite-form" onSubmit={e => { e.preventDefault(); void send(); }}>
      <h2 className="t-h3">Invite a person</h2>
      <p className="t-body-sm">Members can brief and confirm paid work. Owners also manage billing, people and connections.</p>
      <div className="team-form-row">
        <label>Email<input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="person@company.com" required /></label>
        <label>Role<AppSelect
          value={role}
          ariaLabel="Role"
          options={[
            { value: "member", label: "Member" },
            { value: "owner", label: "Owner" },
          ]}
          onChange={(v) => setRole(v as "member" | "owner")}
        /></label>
        <button className="btn btn-brand" type="submit" disabled={busy || Date.now() < retryAt}>{busy ? "Sending…" : "Send invite"}</button>
      </div>
    </form>}

    <div className="rule">People</div>
    <div className="card team-list">
      {!team && !error && <p className="t-body-sm">Loading team…</p>}
      {team?.members.map(member => <div className="team-row" key={member.userId}>
        <div className="team-person"><strong>{member.name || member.email || "Team member"}{member.userId === brand?.self?.id ? " · You" : ""}</strong><span>{member.email}</span>{member.addedBy === "aditor" && <small>Assigned by Aditor</small>}</div>
        <span className="team-role">{member.role}</span>
        {owner && member.role !== "strategist" && member.userId !== brand?.self?.id && <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => void changeMember(member, "role")}>{member.role === "owner" ? "Make member" : "Make owner"}</button>}
        {(owner || member.userId === brand?.self?.id) && <button type="button" className="btn btn-ghost" disabled={busy} onClick={() => void changeMember(member, "remove")}>{member.userId === brand?.self?.id ? "Leave brand" : "Remove"}</button>}
      </div>)}
    </div>

    <div className="rule">Pending invitations</div>
    <div className="card team-list">
      {team?.invites.length === 0 && <p className="t-body-sm">No pending invitations.</p>}
      {team?.invites.map(invite => <div className="team-row" key={invite.id}>
        <div className="team-person"><strong>{invite.email}</strong><span>Expires {new Date(invite.expiresAt).toLocaleDateString()}</span></div>
        <span className="team-role">{invite.role}</span>
        {owner && <><button type="button" className="btn btn-ghost" disabled={busy || Date.now() < retryAt} title="Sends a fresh link and revokes the previous invitation" onClick={() => void send(invite.email, invite.role)}>Resend</button><button type="button" className="btn btn-ghost" disabled={busy} onClick={() => void revoke(invite)}>Revoke</button></>}
      </div>)}
    </div>

    {share && <div className="team-share-overlay" onClick={() => setShare(null)}>
      <div className="team-share card" role="dialog" aria-modal="true" aria-label="Invitation created" onClick={e => e.stopPropagation()}>
        <button className="team-share-close" type="button" aria-label="Close" onClick={() => setShare(null)}>×</button>
        <h2 className="t-h3">Invitation created</h2>
        <p className="t-body-sm">{share.invite.email} · {share.invite.role} · {brand?.name}</p>
        <p className="t-body-sm">{share.mailSent ? "Invitation email was sent." : "Email wasn't delivered. Share this new link directly."}</p>
        <label className="input-label" htmlFor="team-invite-link">Invitation link</label>
        <input ref={linkField} id="team-invite-link" className="team-invite-link" readOnly value={share.inviteUrl} onFocus={e => e.currentTarget.select()} />
        <button className="btn btn-brand" type="button" onClick={() => void copyLink()}>Copy invite link</button>
        {copyFailed && <p className="t-body-sm">Select and copy the link above manually.</p>}
      </div>
    </div>}
  </section>;
}
